import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import sample from "lodash/sample";
import Clickable from "core/Clickable";

// Pool global de elementos de áudio reutilizáveis
const audioPool = new Map();

// URLs dos áudios de transição
const TRANSITION_SOUNDS = [
  require("assets/audio/transition/transition-1.mp3").default,
  require("assets/audio/transition/transition-2.mp3").default,
  require("assets/audio/transition/transition-3.mp3").default,
];

// Pre-carrega os áudios uma única vez
const preloadAudios = (() => {
  let isPreloading = false;
  let preloadPromise = null;

  return () => {
    if (preloadPromise) return preloadPromise;
    if (isPreloading) return Promise.resolve();

    isPreloading = true;
    preloadPromise = Promise.all(
      TRANSITION_SOUNDS.map(async (url) => {
        try {
          // Cria um pool de 3 elementos para cada áudio para evitar conflitos
          const audioElements = [];

          for (let i = 0; i < 3; i++) {
            const audio = new Audio();
            audio.preload = "auto";
            audio.volume = 1;

            // Promise para garantir que o áudio carregou
            await new Promise((resolve, reject) => {
              const handleLoad = () => {
                audio.removeEventListener("canplaythrough", handleLoad);
                audio.removeEventListener("error", handleError);
                resolve();
              };

              const handleError = (error) => {
                audio.removeEventListener("canplaythrough", handleLoad);
                audio.removeEventListener("error", handleError);
                console.warn("Erro ao pre-carregar áudio:", url, error);
                resolve(); // Não rejeitamos para não quebrar outros áudios
              };

              audio.addEventListener("canplaythrough", handleLoad);
              audio.addEventListener("error", handleError);

              audio.src = url;
            });

            audioElements.push(audio);
          }

          audioPool.set(url, audioElements);
          console.log("Áudio pre-carregado:", url);
        } catch (error) {
          console.warn("Falha ao pre-carregar áudio:", url, error);
        }
      })
    );

    return preloadPromise;
  };
})();

// Função para obter um elemento de áudio disponível do pool
const getAvailableAudio = (url) => {
  const pool = audioPool.get(url);
  if (!pool) return null;

  // Encontra um áudio que não está tocando
  const availableAudio = pool.find((audio) => audio.paused);

  if (availableAudio) {
    // Reset para garantir que está limpo
    availableAudio.currentTime = 0;
    return availableAudio;
  }

  // Se todos estão ocupados, para o primeiro e usa ele
  const firstAudio = pool[0];
  if (firstAudio && !firstAudio.paused) {
    firstAudio.pause();
    firstAudio.currentTime = 0;
  }

  return firstAudio;
};

// Função otimizada para tocar áudio
const playTransitionSound = async () => {
  try {
    // Seleciona um áudio aleatório
    const selectedUrl = sample(TRANSITION_SOUNDS);
    const audio = getAvailableAudio(selectedUrl);

    if (!audio) {
      console.warn("Áudio não disponível no pool:", selectedUrl);
      return;
    }

    // Toca o áudio de forma não-bloqueante
    const playPromise = audio.play();

    // Alguns browsers retornam uma Promise, outros não
    if (playPromise && typeof playPromise.catch === "function") {
      playPromise.catch((error) => {
        // Ignora erros comuns de reprodução automática
        if (error.name !== "NotAllowedError") {
          console.warn("Erro ao tocar som de transição:", error);
        }
      });
    }
  } catch (error) {
    console.warn("Erro na reprodução de som:", error);
  }
};

const ClickableSound = ({ onClick, ...rest }) => {
  const isPreloadedRef = useRef(false);

  useEffect(() => {
    // Pre-carrega os áudios quando o componente monta
    if (!isPreloadedRef.current) {
      preloadAudios().then(() => {
        isPreloadedRef.current = true;
      });
    }

    // Cleanup quando o componente desmonta
    return () => {
      // Os áudios ficam no cache global, então não fazemos cleanup aqui
      // para permitir reutilização entre diferentes instâncias
    };
  }, []);

  const handleClick = () => {
    // Executa o onClick original
    onClick();

    // Toca o som de forma não-bloqueante
    playTransitionSound();
  };

  return <Clickable {...rest} onClick={handleClick} />;
};

ClickableSound.propTypes = {
  onClick: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
};

export default ClickableSound;
